import * as THREE from 'three';

/**
 * Calcula a normal do plano de oclusão (pequeno PCA sobre os centróides de todos os dentes,
 * retornando o autovetor de menor valor próprio).
 * @param {THREE.Vector3[]} centroidsLocal — lista de centróides (local‑space) em ordem de arco.
 * @returns {THREE.Vector3} normalLocal
 */
export function computeOcclusalPlane(centroidsLocal) {
  // 1) calcula mean
  const mean = new THREE.Vector3();
  centroidsLocal.forEach(c => mean.add(c));
  mean.divideScalar(centroidsLocal.length);

  // 2) monta covariância (idem)
  let xx=0, xy=0, xz=0, yy=0, yz=0, zz=0;
  centroidsLocal.forEach(c => {
    const v = c.clone().sub(mean);
    xx += v.x*v.x; xy += v.x*v.y; xz += v.x*v.z;
    yy += v.y*v.y; yz += v.y*v.z; zz += v.z*v.z;
  });
  const n = centroidsLocal.length;
  const cov = new THREE.Matrix3().set(
    xx/n, xy/n, xz/n,
    xy/n, yy/n, yz/n,
    xz/n, yz/n, zz/n
  );

  // 3) jacobi e escolhe o auto‑vetor de menor auto‑valor
  const { eigenvectors, eigenvalues } = jacobi(cov);
  let minIdx = 0;
  eigenvalues.forEach((v,i) => { if (v < eigenvalues[minIdx]) minIdx = i; });
  const normal = eigenvectors[minIdx].clone().normalize();

  return { normal, point: mean };
}

export function computeOcclusalPlaneFrom3Points(dentalMap, meshesMap, fdiA, fdiB, fdiC) {
  // recupera os meshes
  const mA = meshesMap[fdiA], mB = meshesMap[fdiB], mC = meshesMap[fdiC];
  [mA,mB,mC].forEach(m => m.updateMatrixWorld(true));

  // pega o centerOfMass local e transforma em world
  const pA = dentalMap[fdiA].centerOfMass.clone().applyMatrix4(mA.matrixWorld);
  const pB = dentalMap[fdiB].centerOfMass.clone().applyMatrix4(mB.matrixWorld);
  const pC = dentalMap[fdiC].centerOfMass.clone().applyMatrix4(mC.matrixWorld);

  const plane  = new THREE.Plane().setFromCoplanarPoints(pA,pB,pC);
  const center = new THREE.Vector3().addVectors(pA,pB).add(pC).divideScalar(3);
  return { plane, normal: plane.normal.clone(), center };
}


/**
 * Para um dente (índice i), retorna os três eixos:
 * - mesioDistal (tangente no arco),
 * - occlusalApical (normal ao plano de oclusão),
 * - buccoLingual (binormal).
 * @param {THREE.Vector3[]} centroidsLocal — centróides de todos os dentes em local-space, ordenados no arco.
 * @param {number} i — índice do dente atual em centroidsLocal.
 * @param {THREE.Vector3} occlusalNormalLocal — normal ao plano de oclusão (local-space).
 * @returns {{ mesioDistal:THREE.Vector3, occlusalApical:THREE.Vector3, buccoLingual:THREE.Vector3 }}
 */
export function findDentalAxes(centroidsLocal, i, occlusalNormalLocal) {
  // pega vizinhos (se for extremidade, duplica)
  const prev = centroidsLocal[i-1] || centroidsLocal[i];
  const next = centroidsLocal[i+1] || centroidsLocal[i];
  // 1) mesio-distal: direção do arco
  const mesioDistal = next.clone().sub(prev).normalize();
  // 2) oclusal-apical: normal ao plano
  const occlusalApical = occlusalNormalLocal.clone().normalize();
  // 3) bucco-lingual: binormal
  const buccoLingual = new THREE.Vector3()
    .crossVectors(mesioDistal, occlusalApical)
    .normalize();
  // re‑ortogonaliza para garantir base ortonormal
  occlusalApical.crossVectors(buccoLingual, mesioDistal).normalize();

  return { mesioDistal, occlusalApical, buccoLingual };
}

/**
 * Desenha no scene um eixo unitário a partir de um centróide de dente.
 * @param {THREE.Scene} scene
 * @param {THREE.Mesh} mesh — usado só para converter local→world.
 * @param {THREE.Vector3} centerLocal — posição do centróide no local-space.
 * @param {{mesioDistal, occlusalApical, buccoLingual}} axes
 * @param {number} length — tamanho das linhas (ex: raio da boundingSphere).
 */
export function plotDentalAxes(scene, mesh, centerLocal, axes, length = 1) {
  // cores: R=mesioDistal, G=buccoLingual, B=occlusalApical
  const colors = {
    mesioDistal:    0xff0000,
    buccoLingual:   0x00ff00,
    occlusalApical: 0x0000ff
  };

  // converte centróide para world-space
  const centerWorld = centerLocal.clone();
  mesh.localToWorld(centerWorld);

  Object.entries(axes).forEach(([key, dir]) => {
    const color = colors[key];
    // garante vetor unitário
    const unitDir = dir.clone().normalize();

    // ponto negativo e positivo
    const negEnd = centerWorld.clone().add(unitDir.clone().multiplyScalar(-length));
    const posEnd = centerWorld.clone().add(unitDir.clone().multiplyScalar( length));

    // desenha linha completa
    const geometry = new THREE.BufferGeometry().setFromPoints([negEnd, posEnd]);
    const material = new THREE.LineBasicMaterial({ color });
    scene.add(new THREE.Line(geometry, material));

    // esferas em ambas as extremidades
    [negEnd, posEnd].forEach(pt => {
      const sphere = new THREE.Mesh(
        new THREE.SphereGeometry(length * 0.05, 12, 12),
        new THREE.MeshStandardMaterial({ color })
      );
      sphere.position.copy(pt);
      scene.add(sphere);
    });
  });
}


/** Jacobi 3×3 (igual ao seu código original) */
function jacobi(matrix) {
  const mat = matrix.clone();
  const eigenvectors = [
    new THREE.Vector3(1,0,0),
    new THREE.Vector3(0,1,0),
    new THREE.Vector3(0,0,1),
  ];
  const maxSweeps = 10, eps = 1e-9;
  for (let sweep = 0; sweep < maxSweeps; sweep++) {
    let maxVal = 0, p = 0, q = 0;
    for (let i = 0; i < 3; i++){
      for (let j = i+1; j < 3; j++){
        const a = Math.abs(mat.elements[i*3 + j]);
        if (a > maxVal) { maxVal = a; p = i; q = j; }
      }
    }
    if (maxVal < eps) break;
    const app = mat.elements[p*3+p],
          aqq = mat.elements[q*3+q],
          apq = mat.elements[p*3+q];
    const theta = 0.5 * Math.atan2(2*apq, app - aqq);
    const c = Math.cos(theta), s = Math.sin(theta);
    // constrói R
    const R = new THREE.Matrix3().identity();
    R.elements[p*3 + p] = c;
    R.elements[q*3 + q] = c;
    R.elements[p*3 + q] = -s;
    R.elements[q*3 + p] = s;
    // mat = R^T * mat * R
    mat.premultiply(R.clone().transpose());
    mat.multiply(R);
    // atualiza vetores
    for (let k = 0; k < 3; k++) {
      const tv = eigenvectors[k].clone();
      eigenvectors[k].set(
        tv.x * R.elements[0] + tv.y * R.elements[3] + tv.z * R.elements[6],
        tv.x * R.elements[1] + tv.y * R.elements[4] + tv.z * R.elements[7],
        tv.x * R.elements[2] + tv.y * R.elements[5] + tv.z * R.elements[8]
      );
    }
  }
  // autovalores nas diagonais
  const eigenvalues = [mat.elements[0], mat.elements[4], mat.elements[8]];
  return { eigenvectors, eigenvalues };
}

/**
 * Adiciona um helper que mostra o plano definido por (normal, point).
 * @param {THREE.Scene} scene
 * @param {THREE.Vector3} normal — normal unitária do plano
 * @param {THREE.Vector3} point  — ponto qualquer sobre o plano
 * @param {number} size         — tamanho do helper
 * @param {number} color        — cor do helper
 */
export function plotOcclusalPlane(scene, normal, point, size = 50, color = 0xffff00) {
  // define a equação do plano: normal.dot(x) + constant = 0
  // constant = - normal ⋅ point
  const constant = - normal.dot(point);
  const plane = new THREE.Plane(normal, constant);

  // helper visual
  const helper = new THREE.PlaneHelper(plane, size, color);
  scene.add(helper);
}
